import { useProgressSummary } from 'components/Forms/useProgressSummary'
import { CommentData, Organization, OrganizationTableItem, Task } from 'types'

export const useBuildTaskTreeRows = () => {
  const calculateProgress = useProgressSummary()
  const buildTaskTreeRows = (tasks: Task[] | [], organizations: OrganizationTableItem[], comments: CommentData[]) => {
    const rows: any[] = []
    const seenTreePaths = new Set<string>()
    const seenRowIds = new Set<string>()

    const topLevelTasks = tasks?.filter((task: Task) => task?.SK?.startsWith('TASK_')) || []
    const subtaskList = tasks?.filter((task: Task) => task?.SK?.startsWith('SUBTASK_')) || []

    topLevelTasks.forEach(task => {
      const subtasks = subtaskList.filter(st => st?.GSI1PK === task?.SK)
      const { progressSummary, chartData: _chartData, completionRatio } = calculateProgress(subtasks, { task })

      const taskId = `${task.PK}::${task.SK}`
      const orgId: string = task?.PK?.split('ORG_')?.[1]
      const relatedOrganization = organizations?.find((org: Organization) => org?.org_uuid === orgId)

      const contractedByIds: string[] = relatedOrganization?.contractedBy || []
      const contractedByOrgs = contractedByIds
        .map(id => organizations?.find(org => org?.org_uuid === id))
        .filter(Boolean)
      const commentCount = comments?.filter((c: CommentData) => c?.PK === task?.SK)?.length ?? 0
      // Ensure top-level treePath is unique
      let topLabel = `${task.name} (${task.SK})`
      let taskPathKey = topLabel
      let k = 1
      while (seenTreePaths.has(taskPathKey)) {
        topLabel = `${task.name} (${task.SK}) (${k})`
        taskPathKey = topLabel
        k++
      }
      const topTreePath = [topLabel]

      const taskRow = {
        ...task,
        id: taskId,
        rowId: taskId,
        treePath: topTreePath,
        isTask: true,
        isParent: true,
        queueRole: task?.queue?.role ?? '',
        progressSummary,
        completionRatio: completionRatio.toFixed(0),
        contractedBy: contractedByOrgs,
        commentCount: commentCount ?? 0,
      }
      rows.push(taskRow)
      seenTreePaths.add(taskPathKey)
      seenRowIds.add(taskId)

      subtasks.forEach(subtask => {
        const subtaskId = `${task.PK}::${subtask.SK}`
        const orgId: string = task?.PK?.split('ORG_')?.[1]
        const relatedOrganization = organizations?.find((org: Organization) => org?.org_uuid === orgId)

        const contractedByIds: string[] = relatedOrganization?.contractedBy || []
        const contractedByOrgs = contractedByIds
          .map(id => organizations?.find(org => org?.org_uuid === id))
          .filter(Boolean)

        // Ensure subtask treePath is unique
        let subLabel = subtask.label || subtask.name || 'Unnamed Subtask'
        let subtaskPathKey = `${topLabel} > ${subLabel}`
        let i = 1
        while (seenTreePaths.has(subtaskPathKey)) {
          subLabel = `${subtask.label || subtask.name} (${i})`
          subtaskPathKey = `${topLabel} > ${subLabel}`
          i++
        }
        const subtaskTreePath = [topLabel, subLabel]

        const subtaskRow = {
          ...subtask,
          rowId: subtaskId,
          parentSK: taskRow.SK,
          parentId: taskRow?.rowId,
          assignees: subtask.assignees ?? [],
          id: subtaskId,
          treePath: subtaskTreePath,
          isSubtask: true,
          queueRole: subtask?.queue?.role ?? '',
          contractedBy: contractedByOrgs,
          commentCount: comments?.filter((c: CommentData) => c?.PK === subtask?.SK)?.length ?? 0,
        }
        rows.push(subtaskRow)
        seenTreePaths.add(subtaskPathKey)
        seenRowIds.add(subtaskId)
      })
    })

    return rows
  }

  return { buildTaskTreeRows }
}
